import { Box, Flex, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import Table from 'components/Table';
import Title from 'components/Title';
import { useIntegrationSyncPolling } from 'hooks/common/useIntegrationSyncPolling';
import { fetchXeroPaginatedPayItem } from 'hooks/integrations/useFetchPaginatedXeroPayItem';
import {
  IXeroTimeeroPayItemSearchQuery,
  useFetchXeroPaginatedTimeeroPayItem,
} from 'hooks/integrations/useFetchPaginatedXeroTimeeroPayItem';
import { IXeroPayItem, useMapXeroPayItem } from 'hooks/integrations/useMapXeroPayItem';
import { useSyncXero } from 'hooks/integrations/useSyncXero';
import { IXeroUnmap, useUnmapXeroMapping } from 'hooks/integrations/useUnMapXeroMapping';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

const initialSearchQuery: IXeroTimeeroPayItemSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const PayItemMapping = () => {
  const [mappedPayItems, setMappedPayItems] = useState<any>(null);
  const [currentMapPayItem, setCurrentMapPayItem] = useState<any>(null);

  const { t } = useTranslation(['common', 'employees', 'integrations']);

  const {
    data: xeroPayItemData,
    status: xeroPayItemStatus,
    refetch,
  } = useFetchXeroPaginatedTimeeroPayItem(initialSearchQuery);
  const { status: xeroMapPayItemStatus, mutateAsync: xeroMapPayItemMutateAsync } = useMapXeroPayItem();
  const { status: xeroUnmapPayItemStatus, mutateAsync: xeroUnmapPayItemMutateAsync } = useUnmapXeroMapping();
  const {
    status: xeroSyncStatus,
    mutateAsync: xeroSyncMutateAsync,
    data: syncData,
    isError: isSyncError,
  } = useSyncXero();

  const { syncLongPollingStatus } = useIntegrationSyncPolling({ id: syncData?.payload?.id, refetch: refetch });

  const columns = [
    {
      Header: t('integrations:xero.timeeroPayType'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: t('integrations:xero.payItemName'),
      accessor: 'payItemName',
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={xeroPayItemsLoadOptions}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.selected_type
                ? data.row.original?.selected_type || null
                : mappedPayItems &&
                    mappedPayItems[`${data.row.original.value}`] &&
                    mappedPayItems[`${data.row.original.value}`].payItem
                  ? mappedPayItems[`${data.row.original.value}`].payItem
                  : null
            }
            onChange={(value: any) => {
              setMappedPayItems((prevValue: any) => ({
                ...prevValue,
                [data.row.original.value]: {
                  payItem: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:xero.selectPayItem')}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      Cell: (data: CellValue) => {
        return data.row.original.selected_type && data.row.original.selected_type ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={xeroUnmapPayItemStatus === 'loading' && currentMapPayItem === data.row.original.value}
            onClick={() => onUnMapPayItem(data.row.original.value, data.row.original.selected_type.id)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={xeroMapPayItemStatus === 'loading' && currentMapPayItem === data.row.original.value}
            onClick={() => onMapPayItem(data.row.original.value)}
          >
            {t('integrations:xero.mapPayItem')}
          </Button>
        );
      },
    },
  ];

  const onMapPayItem = (timeeroPayItemValue: string) => {
    if (
      mappedPayItems &&
      mappedPayItems[timeeroPayItemValue] &&
      mappedPayItems[timeeroPayItemValue].hasOwnProperty('payItem')
    ) {
      setCurrentMapPayItem(timeeroPayItemValue);
      const postData: IXeroPayItem = {
        pay_type: timeeroPayItemValue,
        xero_pay_item_id: parseInt(mappedPayItems[timeeroPayItemValue].payItem.id),
      };
      xeroMapPayItemMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:xero.payItemMapDesc') });
    }
  };

  const onUnMapPayItem = (timeeroPayItemValue: string, xeroPayItemId: number) => {
    setCurrentMapPayItem(timeeroPayItemValue);
    const postData: IXeroUnmap = {
      xero_pay_item_id: xeroPayItemId,
    };
    xeroUnmapPayItemMutateAsync(postData).then(() => {
      setCurrentMapPayItem(null);
      setMappedPayItems((prevValue: any) => ({
        ...prevValue,
        [timeeroPayItemValue]: null,
      }));
      refetch();
    });
  };

  const onSyncXero = () => {
    xeroSyncMutateAsync();
    syncLongPollingStatus.current = 'fetching';
  };

  const xeroPayItemsLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchXeroPaginatedPayItem, { search, page });
  };

  return (
    <Box>
      <Title>{t('integrations:xero.xeroMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={!isSyncError && (xeroSyncStatus === 'loading' || syncLongPollingStatus.current === 'fetching')}
          onClick={onSyncXero}
        >
          {t('integrations:xero.syncOnline')}
        </Button>
      </Flex>

      <Table
        columns={columns}
        data={xeroPayItemData?.payload || []}
        isLoading={xeroPayItemStatus === 'loading'}
        paginationData={xeroPayItemData?.meta_data?.pagination}
      />
    </Box>
  );
};
export default PayItemMapping;
